import React, { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import { Typography, Skeleton, Paper } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@/components/Select";
import { AssignedOrgUnits } from "@/domain/user.interface";
import { Program } from "@/domain/programs.interface";
import { Organization } from "@/domain/organizations.interface";

interface TabAssignOUProps {
  isEditMode?: boolean;
  programs: Program[];
  isLoadingPrograms: boolean;
  selectedProgram?: string;
  onProgramChange: (programId: string) => void;
  organizations: Organization[];
  isLoadingOrganizations: boolean;
  isAccountAdmin: boolean;
  isSystemAdmin: boolean;
  isSupport: boolean;
  isExemptFromOUValidation: boolean;
}

export const TabAssignOU: FC<TabAssignOUProps> = ({
  programs,
  isLoadingPrograms,
  selectedProgram,
  onProgramChange,
  organizations,
  isLoadingOrganizations,
  isAccountAdmin,
  isSystemAdmin,
  isSupport,
  isExemptFromOUValidation,
}) => {
  const { watch, setValue, formState } = useFormContext();

  // AC01: Prepare program options sorted alphabetically
  const programsOptions = useMemo(() => {
    return programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));
  }, [programs]);

  // AC03: Get assigned OUs for selected program (maintains selection when changing program)
  const assignedOrgUnitsWatched = watch(
    "assignedOrgUnits",
    [],
  ) as AssignedOrgUnits[];

  const currentOrgUnits = useMemo(() => {
    if (!selectedProgram) return [];
    const match = assignedOrgUnitsWatched.find(
      (it) => it.programId === selectedProgram,
    );
    return match?.orgUnitIds || [];
  }, [assignedOrgUnitsWatched, selectedProgram]);

  // AC06: Show special message for account admin, system admin, or support
  if (isExemptFromOUValidation) {
    let message = "";
    if (isAccountAdmin) {
      message =
        "* The users with account admin role have access to all programs and org units.";
    } else if (isSystemAdmin) {
      message =
        "* The users with system admin role have access to all programs and org units.";
    } else if (isSupport) {
      message =
        "* The users with support role have access to all programs and org units.";
    } else {
      message =
        "* The users with this role have access to all programs and org units.";
    }

    return (
      <Box>
        <Typography
          variant="bodyMedium"
          sx={{ color: "text.secondary", fontStyle: "italic" }}
        >
          {message}
        </Typography>
      </Box>
    );
  }

  // AC03: Handle OU toggle - maintains selection when changing program
  const handleOrgUnitToggle = (orgUnitId: string) => {
    if (!selectedProgram) return;

    const isChecked = currentOrgUnits.includes(orgUnitId);
    let newValue: string[];
    if (isChecked) {
      newValue = currentOrgUnits.filter((id) => id !== orgUnitId);
    } else {
      newValue = [...currentOrgUnits, orgUnitId];
    }

    // Update assignedOrgUnits maintaining all program selections
    const match = assignedOrgUnitsWatched.find(
      (it) => it.programId === selectedProgram,
    );

    let newAssignedOrgUnits: AssignedOrgUnits[];
    if (match) {
      if (newValue.length === 0) {
        // Remove program entry if no OUs selected
        newAssignedOrgUnits = assignedOrgUnitsWatched.filter(
          (it) => it.programId !== selectedProgram,
        );
      } else {
        // Update existing program entry
        newAssignedOrgUnits = assignedOrgUnitsWatched.map((it) => {
          if (it.programId !== selectedProgram) return it;
          return {
            programId: selectedProgram,
            orgUnitIds: newValue,
          };
        });
      }
    } else {
      // Add new program entry
      newAssignedOrgUnits = [
        ...assignedOrgUnitsWatched,
        {
          programId: selectedProgram,
          orgUnitIds: newValue,
        },
      ];
    }

    setValue("assignedOrgUnits", newAssignedOrgUnits, { shouldValidate: true });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* AC01: Program Dropdown */}
      <Box sx={{ width: "100%" }}>
        {isLoadingPrograms ? (
          // AC00-D: Loading skeleton
          <Skeleton variant="rectangular" height={56} />
        ) : (
          <Select
            id="program-select"
            label="Program"
            value={selectedProgram || ""}
            handleChange={(value) => {
              onProgramChange(value as string);
            }}
            data={programsOptions}
            width="100%"
          />
        )}
      </Box>

      {/* AC02: OU List with Checkboxes */}
      {selectedProgram && (
        <Box sx={{ width: "100%" }}>
          {isLoadingOrganizations ? (
            // AC00-D: Loading skeletons
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
            </Box>
          ) : organizations.length === 0 ? (
            <Typography variant="bodyMedium" sx={{ color: "text.secondary" }}>
              No organizational units available for this program.
            </Typography>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {organizations.map((org) => {
                  const isChecked = currentOrgUnits.includes(org._id);
                  return (
                    <FormControlLabel
                      key={org._id}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleOrgUnitToggle(org._id)}
                        />
                      }
                      label={org.name}
                      sx={{
                        alignItems: "center",
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                          ml: 0.5,
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* AC04: Show validation error message */}
      {formState.errors.assignedOrgUnits && (
        <Typography variant="bodySmall" sx={{ color: "error.main" }}>
          {typeof formState.errors.assignedOrgUnits.message === "string"
            ? formState.errors.assignedOrgUnits.message
            : "At least one organizational unit must be selected for this user."}
        </Typography>
      )}
    </Box>
  );
};
